import type { ProseMirrorContent } from "@shared/models/card-stack"
import { getCardTitle, replaceCardPreservingIdentity } from "@shared/unified-tiptap/util/card"
import { Fragment, type Node as ProseMirrorNode } from "@tiptap/pm/model"
import { Transform } from "@tiptap/pm/transform"
import { IdList, type ElementId } from "articulated"
import { parseCardStackProseMirrorJson } from "./prose-mirror-utils"

interface CardSegment {
  node: ProseMirrorNode
  offset: number
  ids: ElementId[]
}

export function mergeFollowedLessonContent(
  destination: ProseMirrorContent,
  donor: ProseMirrorContent,
): ProseMirrorContent {
  const destinationDoc = parseCardStackProseMirrorJson(destination.json)
  const donorDoc = parseCardStackProseMirrorJson(donor.json)
  const destinationIds = loadMatchingIds(destination, destinationDoc)
  const donorIds = loadMatchingIds(donor, donorDoc)
  const destinationCards = cardSegments(destinationDoc, destinationIds)
  const donorCards = cardSegments(donorDoc, donorIds)

  const matches = matchDestinationCards(destinationCards, donorCards)
  const matchedDestinationCards = new Set(
    matches.filter((card): card is CardSegment => card !== undefined),
  )
  const insertedCards = donorCards.map((donorCard, index) => {
    const destinationCard = matches[index]
    return destinationCard === undefined
      ? donorCard.node
      : replaceCardPreservingIdentity(destinationCard.node, donorCard.node)
  })

  const transform = new Transform(destinationDoc)
  for (const destinationCard of [...matchedDestinationCards].sort(
    (left, right) => right.offset - left.offset,
  )) {
    transform.delete(destinationCard.offset, destinationCard.offset + destinationCard.node.nodeSize)
  }
  transform.insert(0, Fragment.fromArray(insertedCards))
  const mergedDoc = transform.doc

  let mergedIds = destinationIds
  for (const destinationCard of [...matchedDestinationCards].sort(
    (left, right) => right.offset - left.offset,
  )) {
    mergedIds = replaceIds(
      mergedIds,
      destinationCard.offset,
      destinationCard.offset + destinationCard.node.nodeSize,
      [],
    )
  }
  mergedIds = replaceIds(
    mergedIds,
    0,
    0,
    donorCards.flatMap(({ ids }) => ids),
  )

  if (mergedIds.length !== mergedDoc.content.size) {
    throw new Error("Merged ProseMirror content and element IDs have different lengths")
  }
  return { json: mergedDoc.toJSON(), ids: mergedIds.save() }
}

/** For each donor card, the destination card with the same title that it replaces, if any. */
function matchDestinationCards(
  destinationCards: CardSegment[],
  donorCards: CardSegment[],
): Array<CardSegment | undefined> {
  const destinationCardsByTitle = new Map<string, CardSegment[]>()
  destinationCards.forEach((card) => {
    const titleKey = normalizedTitleKey(card.node)
    if (titleKey === null) return
    const matches = destinationCardsByTitle.get(titleKey) ?? []
    matches.push(card)
    destinationCardsByTitle.set(titleKey, matches)
  })

  return donorCards.map((donorCard) => {
    const titleKey = normalizedTitleKey(donorCard.node)
    return titleKey === null ? undefined : destinationCardsByTitle.get(titleKey)?.shift()
  })
}

function loadMatchingIds(content: ProseMirrorContent, doc: ProseMirrorNode): IdList {
  const ids = IdList.load(content.ids)
  if (ids.length !== doc.content.size) {
    throw new Error("ProseMirror content and element IDs have different lengths")
  }
  return ids
}

function cardSegments(doc: ProseMirrorNode, ids: IdList): CardSegment[] {
  const presentIds = Array.from(ids.values())
  const cards: CardSegment[] = []
  doc.forEach((node, offset) => {
    cards.push({ node, offset, ids: presentIds.slice(offset, offset + node.nodeSize) })
  })
  return cards
}

function normalizedTitleKey(card: ProseMirrorNode): string | null {
  if (!card.type.isInGroup("card") || card.type.name === "untitledCard") return null
  const title = getCardTitle(card)?.replace(/\s/gu, "") ?? ""
  return title.length === 0 ? null : title
}

function replaceIds(ids: IdList, from: number, to: number, inserted: ElementId[]): IdList {
  const before = from === 0 ? null : ids.at(from - 1)
  let nextIds = ids.deleteRange(from, to)
  let cursor = before
  for (const id of inserted) {
    nextIds = nextIds.insertAfter(cursor, id)
    cursor = id
  }
  return nextIds
}
